import React from 'react';
import {
  Card, BlockStack, InlineStack, Text, Badge, Button, Box,
} from '@shopify/polaris';
import { XIcon } from '@shopify/polaris-icons';

function keyOf(article, color) {
  return `${article}|${color}`;
}

/**
 * Per-SKU summary cards. Most-recently-added on top. Clicking a card makes it
 * the active SKU (drives the calendar) WITHOUT reordering the stack. The
 * active card gets a translucent background in the same hue as its calendar
 * bars (so users can see at a glance which card the calendar belongs to).
 */
export default function SkuStackPane({
  stack, details, onRemove, onActivate, activeArticle, activeColor, activeSkuColor,
}) {
  if (stack.length === 0) return null;
  return (
    <BlockStack gap="300">
      {stack.map(({ article, color }) => {
        const d = details[keyOf(article, color)];
        const isActive = article === activeArticle && color === activeColor;
        // Translucent tint = anchor hex + alpha. Keeps text readable while
        // making the active card unmistakable. Falls back to a neutral tint
        // if the anchor color isn't known yet (e.g., detail still loading).
        const tint = isActive
          ? (activeSkuColor ? `${activeSkuColor}55` : '#f1f1f1')
          : undefined;
        return (
          <div
            key={keyOf(article, color)}
            className={`sku-history-card${isActive ? ' sku-history-card--active' : ''}`}
            onClick={() => onActivate(article, color)}
            style={{
              cursor: 'pointer',
              borderRadius: 12,
              backgroundColor: tint,
            }}
          >
            <Card>
              <InlineStack align="space-between" blockAlign="start" wrap={false}>
                <Box>
                  <BlockStack gap="100">
                    <Text variant="bodyMd" fontWeight="semibold" as="p">
                      {d?.description ?? `${article} · ${color}`}
                    </Text>
                    <Text variant="bodySm" tone="subdued" as="p">
                      {article} · {color}
                      {d ? ` · ${d.business_segment}` : ''}
                    </Text>
                    {d && <Box><Badge tone={d.removed ? 'critical' : undefined}>{d.map_tier}</Badge></Box>}
                  </BlockStack>
                </Box>
                <div onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="tertiary"
                    icon={XIcon}
                    onClick={() => onRemove(article, color)}
                    accessibilityLabel={`Remove ${article} ${color}`}
                  />
                </div>
              </InlineStack>
            </Card>
          </div>
        );
      })}
    </BlockStack>
  );
}
